import React, { useMemo, useEffect, useState, useRef } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useNotify, Title } from 'react-admin';
import { dataProvider } from '../../DataProvider';
import { formatDateTime, formatLocalTime, toLocalTime } from '../../components/ex_dayjs';
import AgGrid from '../../components/ag-grid/ag-grid';
import RenderControls from '../../components/ra-list/RenderControls';
import { loadSourceType, setDefaultValuesFromURL } from '../../utils/common';
import { Countries, ExchangeMarket } from '../../utils/enum';
import { Button, Tooltip, Link, useColorScheme } from '@mui/material';
import MultiSelectTags from './components/MultiSelectTags';
import Tags from './components/Tags';
import { blue, grey, red, green, deepOrange } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
import { reloadPage } from '../../utils/common';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxHeader from './components/CheckBoxHeader';
import ExactMatch from './components/ExactMatchHeader';
import './style.css';
import { dayjs } from '../../components/ex_dayjs';
import { FilterListProvider } from '../../context/FilterListProvider';
import ApproveButton from './components/approve-button';
import { REASON_TAGS } from '../listings-mapping/formatters/constants';

const SelectButton = styled(Button)(() => ({
    background: '#f0ad4e',
    color: 'white',
    borderColor: '#eea236',
    '&:hover': {
        background: '#ec971f',
        borderColor: '#d58512'
    }
}));

let highlightCell = true;


const EventMatcherList = () => {
    const notify = useNotify();
    const emptyEventMessage = (message) => notify(message || 'Please select event', { type: 'warning', autoHideDuration: 60000 }, { anchorOrigin: { vertical: 'top', horizontal: 'right' } });
    const [tagSelected, setTagsSelected] = useState([]);
    const [remove, setRemove] = useState(false);
    const [common, setCommon] = useState({ quickDeleteTag: false });
    const refbtnFilter = useRef(null);
    const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
    const gridRef = useRef();
    const eventRef = useRef();
    const venueRef = useRef();
    const cityRef = useRef();
    const stateRef = useRef();
    const dateRef = useRef();

    const { mode } = useColorScheme();
    const partialMatchStyle = mode === 'light' ? { backgroundColor: 'orange', color: 'black', padding: '2px' } : { backgroundColor: 'orange', color: 'white', padding: '2px' };
    const noMatchStyle = { backgroundColor: '#ff0000b8', color: 'white', padding: '2px' };


    const getStyles = (tagName) => {
        switch (tagName) {
            case 'high value':
                return green[500];
            case 'low value':
                return red[600];
            case 'support ticket':
                return blue[500];
            case 'skip':
            case 'unmapped':
            case 'manual review':
                return grey[500];
            default:
                return deepOrange[400];
        }
    };

    const defaultTags = ['New', 'Processing', 'High value', 'Low value', 'Request from exchange', 'Support Ticket', 'Skip', 'Not found', 'Manual Review', 'Unmapped', 'Ontario', 'Vegas', 'Request Again'];
    let doLoadData = true;
    const [marketType, setMarketType] = useState<any[]>([]);

    const [defaultValueFilter, setDefaultValuesFilter] = useState({
        MarketId_eq: 1,
        SkipTag: true,
        ManualReviewTag: true,
        Score: [2, 5]
    });

    const handleSelection = (e) => {
        e && setTagsSelected(e.value);
    };

    const SetHighLight = (isHighLight) => {
        highlightCell = isHighLight;
        gridRef.current.getAgGridInstance().api.redrawRows();
    };

    const controls = [
        { component: 'number', name: 'TicketId', label: 'Ticket ID' },
        { component: 'number', name: 'SmOriginalID', label: 'Sm EventID' },
        { component: 'select', name: 'Type_eq', label: 'Type', choices: marketType },
        { component: 'text', name: 'SiteId_contains', label: 'Site ID', xs: 1 },
        { component: 'select', name: 'Country', label: 'Country', choices: Countries },
        { component: 'text', name: 'Name', label: 'Event', xs: 1.5 },
        { component: 'text', name: 'Venue', label: 'Venue', xs: 1.5 },
        { component: 'number', name: 'VenueId_eq', label: 'Venue ID', xs: 1 },
        { component: 'daterange', name: 'DateTime', label: 'Date Time' },
        { component: 'numberrange', name: 'Score', label: 'Score', xs: 1, decimal: 3 },
        { component: 'select', name: 'MarketId_eq', label: 'Exchange market', required: true, isEmptyOption: false, xs: 1, choices: ExchangeMarket },
        { component: 'text', name: 'RuleId', label: 'Rule ID', xs: 1 },
        { component: 'autocomplete', name: 'Tags_contains', label: 'Tags', xs: 2, choices: defaultTags, getStyles: getStyles, onChange: handleSelection },
        { component: 'select', name: 'AdminSkip', label: 'Tagged by', xs: 0.8, choices: [{ id: true, name: 'Admin' }, { id: false, name: 'System' }] },
        { component: 'daterange', name: 'UpdatedOn', label: 'Updated On', isUTC: true },
        { component: 'checkbox', name: 'SkipTag', label: 'Skip tags', xs: 0.8 },
        { component: 'checkbox', name: 'ManualReviewTag', label: 'Manual Review', xs: 0.8 }
    ];

    useEffect(() => {
        if (doLoadData) {
            loadSourceType(dataProvider, (response) => {
                setMarketType(response);
            });

            doLoadData = false;
        }

        if (location.search) {
            let newDefaultValue = { ...defaultValueFilter };
            setDefaultValuesFromURL(location.search, newDefaultValue, controls);
            if (newDefaultValue.EventId) {
                newDefaultValue.id = parseInt(newDefaultValue.EventId);
                delete newDefaultValue.EventId;
            }
            setDefaultValuesFilter(newDefaultValue);
            setTimeout(() => refbtnFilter.current.click(), 500);
        }
    }, []);

    const defaultColDef = useMemo(() => {
        return {
            cellStyle: { wordBreak: 'normal', fontSize: '14px' },
            singleClickEdit: true,
            sortable: true,
            wrapText: true,
            filter: false,
            autoHeight: true,
            flex: 1,
            suppressRowHoverHighlight: true,
            suppressHorizontalScroll: true,
            suppressMovable: true,//block move columns
            resizable: true,
            autoHeaderHeight: true,
        };
    }, []);

    const ragRenderer = (params) => {
        switch (params.column?.colId) {
            case 'siteId':
                return <Tooltip arrow title={params.data.siteId}><span>{params.value}</span></Tooltip>;
            case 'dateTime':
            case 'smDateTime':
                let _date = dayjs(params.data.dateTime);
                let _smdate = dayjs(params.data.smDateTime);
                let isSameDay = _date.isSame(_smdate, 'day');
                let isSameTime = _date.isSame(_smdate, 'hour') && _date.isSame(_smdate, 'minute');
                let validRange = Math.abs(_date.diff(_smdate, 'hour')) === 1;
                return <span style={highlightCell ? ((isSameDay && isSameTime) ? {} : ((isSameDay && validRange) ? partialMatchStyle : noMatchStyle)) : {}}>{formatDateTime(params.value)}</span>;
            case 'name': {
                const isRenamed = params.data.rename_Event !== '';
                const baseStyle = !highlightCell
                    ? undefined
                    : params.data.s_Event === 0
                        ? noMatchStyle
                        : params.data.s_Event < 1
                            ? partialMatchStyle
                            : {};
                const style = isRenamed ? { ...baseStyle, color: 'aqua' } : baseStyle;
                return (
                    <Tooltip arrow title={isRenamed ? `Renamed from "${params.data.name}"` : params.data.name}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5px' }}>
                            <Link href={params.data.url} underline="hover" target="_blank">
                                <span style={style}>
                                    {params.data.rename_Event || params.data.name}
                                </span>
                            </Link>
                            <span style={{ fontSize: '13px', color: blue[500] }}>[{params.data.type}]</span>
                        </div>
                    </Tooltip>
                );
            }
            case 'smEvent':
                return (
                    <Link href={params.data.smUrl} underline="hover" target="_blank">
                        <span style={!highlightCell ? undefined : params.data.s_Event === 0 ? noMatchStyle : params.data.s_Event < 1 ? partialMatchStyle : {}}>{params.value}</span>
                    </Link>
                );
            case 'venue': {
                const isRenamed = params.data.rename_Venue !== '';
                const baseStyle = !highlightCell
                    ? undefined
                    : params.data.s_Venue === 0
                        ? noMatchStyle
                        : params.data.s_Venue < 1
                            ? partialMatchStyle
                            : {};
                const style = isRenamed ? { ...baseStyle, color: 'aqua' } : baseStyle;
                return (
                    <Tooltip arrow title={isRenamed ? `Renamed from "${params.data.venue}"` : params.data.venue}>
                        <span style={style}>
                            {params.data.rename_Venue || params.data.venue}
                        </span>
                    </Tooltip>
                );
            }
            case 'smVenue':
                return <Tooltip arrow title={params.data.smVenue}><span style={!highlightCell ? undefined : params.data.s_Venue === 0 ? noMatchStyle : params.data.s_Venue < 1 ? partialMatchStyle : {}}>{params.value}</span></Tooltip>;
            case 'city': {
                const isRenamed = params.data.rename_City !== '';
                const baseStyle = !highlightCell
                    ? undefined
                    : params.data.s_City === 0
                        ? noMatchStyle
                        : params.data.s_City < 1
                            ? partialMatchStyle
                            : {};
                const style = isRenamed ? { ...baseStyle, color: 'aqua' } : baseStyle;
                return (
                    <Tooltip arrow title={isRenamed ? `Renamed from "${params.data.city}"` : params.data.city}>
                        <span style={style}>
                            {params.data.rename_City || params.data.city}
                        </span>
                    </Tooltip>
                );
            }
            case 'smCity':
                return (
                    <Tooltip arrow title={params.data.smCity}>
                        <span style={!highlightCell ? undefined : params.data.s_City === 0 ? noMatchStyle : params.data.s_City < 1 ? partialMatchStyle : {}}>
                            {params.value}
                        </span>
                    </Tooltip>
                );
            case 'state': {
                const isRenamed = params.data.rename_State !== '';
                const baseStyle = !highlightCell
                    ? undefined
                    : params.data.s_State === 0
                        ? noMatchStyle
                        : params.data.s_State < 1
                            ? partialMatchStyle
                            : {};
                const style = isRenamed ? { ...baseStyle, color: 'aqua' } : baseStyle;
                return (
                    <Tooltip arrow title={isRenamed ? `Renamed from "${params.data.state}"` : params.data.state}>
                        <span style={style}>
                            {params.data.rename_State || params.data.state}
                        </span>
                    </Tooltip>
                );
            }
            case 'smState':
                return (
                    <Tooltip arrow title={params.data.smState}>
                        <span style={!highlightCell ? undefined : params.data.s_State === 0 ? noMatchStyle : params.data.s_State < 1 ? partialMatchStyle : {}}>
                            {params.value}
                        </span>
                    </Tooltip>
                );
            default:
                return <span>{params.value}</span>;
        }
    };

    const colDefs = useMemo(() => [
        { field: '', headerName: '', checkboxSelection: true, headerCheckboxSelection: true, maxWidth: 30 },
        { field: 'siteId', headerName: 'SiteId', cellRenderer: ragRenderer, maxWidth: 80, cellClass: 'myag-cell-wrap-text' },
        { field: 'venueId', headerName: 'VenueId', maxWidth: 80, cellClass: 'myag-cell-wrap-text' },
        {
            headerName: 'Exact Matches',
            headerGroupComponent: ExactMatch,
            headerGroupComponentParams: { exactRef: eventRef },
            children: [
                {
                    field: 'name', headerName: 'Event', minWidth: 242, wrapText: true, suppressSizeToFit: true,
                    cellRenderer: ragRenderer
                    , columnGroupShow: 'open'
                },
                {
                    field: 'smEvent', headerName: 'SmEvent', minWidth: 242, wrapText: true, suppressSizeToFit: true,
                    cellRenderer: ragRenderer,
                    columnGroupShow: 'open'
                },
            ],
        },
        {
            headerName: 'Exact Matches',
            headerGroupComponent: ExactMatch,
            headerGroupComponentParams: { exactRef: venueRef },
            children: [
                {
                    field: 'venue', headerName: 'Venue', minWidth: 140, wrapText: true, width: 140,
                    cellRenderer: ragRenderer,
                    columnGroupShow: 'open'

                },
                {
                    field: 'smVenue', headerName: 'Sm-Venue', minWidth: 140, wrapText: true, autoHeight: true, width: 140,
                    cellRenderer: ragRenderer,
                    columnGroupShow: 'open'
                },
            ],
        },
        {
            headerName: 'Exact Matches',
            headerGroupComponent: ExactMatch,
            headerGroupComponentParams: { exactRef: cityRef },
            children: [
                {
                    field: 'city', headerName: 'City', minWidth: 85, maxWidth: 95,
                    cellRenderer: ragRenderer,
                    columnGroupShow: 'open'

                },
                {
                    field: 'smCity', headerName: 'Sm-City', minWidth: 85, maxWidth: 95, headerClass: 'myag-header-cell-wrap-text',
                    cellRenderer: ragRenderer,
                    columnGroupShow: 'open',
                }
            ],
        },
        {
            headerName: 'Exact Matches',
            headerGroupComponent: ExactMatch,
            headerGroupComponentParams: { exactRef: stateRef },
            children: [
                {
                    field: 'state', headerName: 'State', width: 50, maxWidth: 50,
                    cellRenderer: ragRenderer,
                    columnGroupShow: 'open'
                },
                {
                    field: 'smState', headerName: 'Sm State', width: 50, maxWidth: 50, headerClass: 'myag-header-cell-wrap-text',
                    cellRenderer: ragRenderer,
                    columnGroupShow: 'open',
                },
            ],
        },
        {
            headerName: 'Exact Matches',
            headerGroupComponent: ExactMatch,
            headerGroupComponentParams: { exactRef: dateRef },
            children: [
                {
                    field: 'dateTime', headerName: 'DateTime', maxWidth: 85,
                    valueParser: params => { return formatDateTime(params.value); },
                    cellRenderer: ragRenderer,
                },
                {
                    field: 'smDateTime', headerName: 'Sm DateTime', maxWidth: 90, headerClass: 'myag-header-cell-wrap-text',
                    valueParser: params => { return formatDateTime(params.value); },
                    cellRenderer: ragRenderer,
                },
            ],
        },
        {
            headerName: 'Highlight',
            headerGroupComponent: CheckBoxHeader,
            headerGroupComponentParams: { SetHighLight: SetHighLight },
            children: [
                { field: 'score', headerName: 'Score', type: 'numericColumn', minWidth: 50, maxWidth: 50 },
                { field: 'listingCount', headerName: 'Volume', type: 'numericColumn', width: 80, maxWidth: 80 },
                { field: 'updatedOn', headerName: 'Updated On', headerClass: 'myag-header-cell-wrap-text',
                    valueFormatter: params => { return formatDateTime(toLocalTime(params.value)); },
                    maxWidth: 85
                },
                {
                    field: 'reasonTags', headerName: 'Reason Tags', maxWidth: 90, headerClass: 'myag-header-cell-wrap-text', cellClass: 'myag-cell-wrap-text',
                    tooltipValueGetter: (params) => REASON_TAGS.find(x => x.id === params.value)?.name
                },
                {
                    field: 'tags', headerName: 'Tags',
                    cellEditor: MultiSelectTags,
                    cellEditorParams: { reloadPage: () => reloadPage('EventMatcher', refbtnFilter), common },
                    editable: true,
                    cellRenderer: Tags,
                    cellRendererParams: { reloadPage: () => reloadPage('EventMatcher', refbtnFilter), setCommon },
                    cellEditorPopup: true,
                    colId: 'tags',
                    cellEditorPopupPosition: 'over',
                    tooltipValueGetter: (params) => params.data.updatedBy ? 'Tagged by: ' + params.data.updatedBy : null,
                },
                {
                    field: 'ruleId', headerName: 'Applied Rules', width: 120, maxWidth: 120, headerClass: 'myag-header-cell-wrap-text',
                    valueFormatter: (params) => {
                        if (!params.value) return '';
                        const rules = params.value.split(',').filter(r => r.trim() !== '');
                        if (rules.length <= 2) return rules.join(', ');
                        return `${rules.slice(0, 2).join(', ')}...`;
                    },
                    tooltipValueGetter: (params) => {
                        if (!params.value) return '';
                        return `Applied Rules: ${params.value}`;
                    }
                }
            ],
        },
    ]);

    const sortingOrder = useMemo(() => {
        return ['desc', 'asc'];
    }, []);

    const [defaultValues, setDefaultValues] = useState({
        sortField: { field: 'listingCount', order: 'desc' },
    });

    const handleSearch = (filterList) => {
        if (eventRef.current.checked) filterList.S_Event_eq = 1;
        if (venueRef.current.checked) filterList.S_Venue_eq = 1;
        if (cityRef.current.checked) filterList.S_State_eq = 1;
        if (stateRef.current.checked) filterList.S_City_eq = 1;
        if (dateRef.current.checked) filterList.S_DateTime_eq = 1;
        gridRef.current.customFilterSearch(filterList);
    };

    const handleSetTags = () => {
        const tags = Array.from(tagSelected).join(',');
        const rowsSelect = gridRef.current.getAgGridInstance().api.getSelectedRows();

        if (rowsSelect.length === 0) {
            emptyEventMessage();
            return;
        } else if (tagSelected.length === 0) {
            emptyEventMessage('Please select tag');
            return;
        }
        if (confirm(`Are you sure to ${remove ? 'remove' : 'set'} ${tagSelected.length} selected Tags`)) {
            rowsSelect.forEach(x => {
                x.tags = tags;
                x.IsRemove = remove;
            });

            dataProvider.postData('EventMatcher', 'SetTagsSelected', rowsSelect)
                .then((response: any) => {
                    if (response.status !== 200 && response.data) {
                        notify('Set Tags successfully', { type: 'success', autoHideDuration: 2000 });
                        reloadPage('EventMatcher', refbtnFilter);
                    }
                })
                .catch((_: any) => {
                    notify('Something went wrong', { type: 'error', autoHideDuration: 5000 });
                });
        }
    };

    const handleApprove = () => {
        const rowsSelect = gridRef.current.getAgGridInstance().api.getSelectedRows();
        if (rowsSelect.length === 0) {
            emptyEventMessage();
            return;
        }
        let skipEvts = rowsSelect.filter((x: any) => x.tags.includes('Skip'));
        if (skipEvts.length) {
            let mesg = 'Please remove events with the [skip] tag before APPROVING:\n\n' + skipEvts.map((x: any) => x.name).join('.\n') + '.';
            alert(mesg);
            return;
        }
        if (confirm(`Are you sure to approve ${rowsSelect.length} selected rows`)) {
            dataProvider.postData('EventMatcher', 'ApproveSelected', rowsSelect).then((response: any) => {
                if (response.status !== 200 && response.data) {
                    notify('Updated Successfully', { type: 'success', autoHideDuration: 2000 });
                    reloadPage('EventMatcher', refbtnFilter);
                }
            })
                .catch((_: any) => {
                    notify('Something went wrong', { type: 'error', autoHideDuration: 5000 });
                });
        }
    };

    const onGridReady = ((params) => {
        let defaultSortModel = [
            { colId: 'listingCount', sort: 'desc', sortIndex: 0 },
        ];
        params.columnApi.applyColumnState({ state: defaultSortModel });
    });

    return (
        <div style={containerStyle}>
            <Title title={'Event Matcher'}></Title>
            <FilterListProvider>
                <RenderControls
                    actions={<>
                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox checked={remove} onChange={() => setRemove(!remove)} />} label="Remove Tags" />
                        </FormGroup>
                        <SelectButton onClick={handleSetTags}>Tag selected</SelectButton>
                        <ApproveButton callback={handleApprove} />
                    </>}
                    controls={controls}
                    defaultValues={defaultValueFilter}
                    onSearch={handleSearch}
                    refFilter={refbtnFilter}
                    agSource={'EventMatcher'}
                    agSort={defaultValues.sortField}
                    exportFile={{
                        filename: `EventMatcher-${formatLocalTime(new Date(), 'YY-MM-DD')}`,
                        columns: ['Type', 'SiteId', 'VenueId', 'Name', 'SmEvent', 'Venue', 'SmVenue', 'City', 'SmCity', 'State', 'SmState',
                            'DateTime', 'SmDateTime', 'Score', 'ListingCount', 'Tags', 'S_Event', 'S_Venue', 'S_State', 'S_City', 'S_DateTime', 'RuleId'],
                        limit: 1000000
                    }}
                />
            </FilterListProvider>

            <AgGrid
                className='event-matcher-grid'
                rowSelection={'multiple'}
                ref={gridRef}
                suppressRowClickSelection={true}
                rowMultiSelectWithClick={true}
                list={'EventMatcher'}
                columnDefs={colDefs}
                sortingOrder={sortingOrder}
                defaultColDef={defaultColDef}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                onGridReadyCallback={onGridReady}
                gridOptions={{ tooltipShowDelay: 200 }}
                // editType={'fullRow'}
            ></AgGrid>
        </div>
    );
};


export default EventMatcherList;
